<template>
	<div v-if="visible">
		<el-dialog :visible.sync="visible"
			:title="$t('privateNetInstance.reInstallTitle')"
			:close-on-click-modal="false"
			:before-close="handleClose" class="re-system">
			<div v-if = "retailTipChange==='retail'" class="tip-warning mb20 lh20" 
				v-html="$t('privateNetInstance.reInstallTip1')">
			</div>
			<div v-else class="tip-warning mb20 lh20" 
				v-html="$t('privateNetInstance.reInstallTip1')+$t('privateNetInstance.reInstallTip2')">
			</div>
			<h5 class="title-position">
				{{ $t('listPage.text.hostInformation') | withColon }}
			</h5>
			<el-table :data="tableData">
				<el-table-column :label="$t('privateNetInstance.instanceName')"
					prop="name"
					width="200">
					<template slot-scope="scope">
						<el-tooltip class="item" effect="dark" :content="scope.row.name"
							placement="bottom">
						<span class="custom-ellipsis">
							{{scope.row.name}}
						</span>
						</el-tooltip>
					</template>
				</el-table-column>
				<el-table-column :label="$t('privateNetInstance.sysDisk')"
					prop="system" width="220">
				</el-table-column>
				<el-table-column :label="$t('privateNetInstance.chargeMethod')"
					prop="chargeType">
				</el-table-column>
				<el-table-column :label="$t('privateNetInstance.expireTime')"
					prop="expire" v-if="chargeModeType==='prepaid_by_duration'"
					min-width="150">
				</el-table-column>
			</el-table>
			<el-form :model="form" ref="form"
				:rules="rules"
				label-width="120px" class="form-position">
				<el-form-item :label="$t('privateNetInstance.mirrorType') | withColon">
					<el-radio-group v-model="selectedImageType">
						<el-radio-button 
							v-for="(item,key,index) in imageTypes" :key="index"
							:label="key">{{item.sourceName}}
						</el-radio-button>
					</el-radio-group>
				</el-form-item>
				<el-form-item :label="$t('privateNetInstance.operateSystem') | withColon">
					<el-radio-group v-model="form.operate"
						@change="systemChange">
						<el-radio-button v-for="(os, index) in form.oss"
							:key="index"
							:label="os">
						</el-radio-button>
					</el-radio-group>
				</el-form-item>
				<el-form-item :label="$t('privateNetInstance.systemVersion') | withColon"
					prop="osTypeId">
					<el-select v-model="form.osTypeId" size="small" @change="getSelectOsId">
						<el-option v-for="(select, index) in form.osTypes[form.operate]"
							:key="index"
							:label="select.osName"
							:value="select.osTypeId">								
						</el-option>
					</el-select>
				</el-form-item>
				<el-form-item :label="$t('create.text.sysDiskCapacity') | withColon">
					<span class="c333">{{sysDiskCapacity}}</span>
					<span class="el-form-item-help">{{$t('create.text.sysDiskCapacityUnitTip')}}</span>
				</el-form-item>
				<el-form-item id="systemPartition" 
					:label="$t('retailInstance.text.systemDataPartitions') | withColon">
					<template v-if="systemType === 'windows'">
						<span>{{$t('retailInstance.text.emptyPartition')}}</span>
					</template>
					<template v-else>
						<el-radio-group v-model="selectedPartitionMode">
							<el-radio v-for="(item,index) in systemDiskPartitionModes" :key="index"
								:label="item.value">
								{{item.label}}
							</el-radio>
						</el-radio-group>
						<el-tooltip class="ml10" effect="dark" placement="right"
							content="Right Top">
							<div slot="content" class="my-os-disk-tip">{{$t('create.tooltip.systemPartition')}}
							</div>
							<a><i class="jc-icon-question"></i></a>
						</el-tooltip>
						<section 
							class="defaultPartition"
							v-if="selectedPartitionMode === 'default'"
							v-html="defaultSystemDataPartitions">
						</section>
						<custom-partition
							ref="systemPartition"
							:visible="selectedPartitionMode === 'custom'"
							:region="rowInfo.region"
							diskType="system"
							:partitionInfos="systemDataPartitions"
							:otherPartitionInfos="dataDiskPartitions"
							:isCentOS66="isCentOS66"
							:totalDiskSize="systemDiskSize">
						</custom-partition>
					</template>
				</el-form-item>
				<el-form-item :label="$t('privateNetInstance.manager') | withColon">
					<span>{{ 'root' }}</span>
				</el-form-item>
				<el-form-item :label="$t('privateNetInstance.hostname') | withColon"
					prop="hostname">
					<el-input :placeholder="$t('privateNetInstance.manageTip')"
						v-model="form.hostname"></el-input>
					<p slot="hint">{{ $t('create.help.mainServerNameRule') }}</p>
				</el-form-item>
				<div v-if="instanceType==='retail'">
					<retail-login-methods @getPassword="getPassword"
					:region="rowInfo.region"
					class="basic-resys-login"
					ref="loginMethods"></retail-login-methods>
				</div>
				<div v-else>
					<login-methods @getPassword="getPassword"
					:region="rowInfo.region"
					:operate="form.operate"
					class="basic-resys-login"
					ref="loginMethods"></login-methods>
				</div>
				<el-form-item 
					v-if="rowInfo.dataRaidTypeId"
					:label="$t('privateNetInstance.dataDisk') | withColon">
					<el-checkbox v-model="form.checked">
						{{ $t('privateNetInstance.formatDataDisk') }}
					</el-checkbox>
					<br/>
					<span v-if="form.checked" class="ff0">
						{{ $t('listPage.speechCraft.dataFormattedRedWarning') }}
					</span>
				</el-form-item>
				<el-form-item :label="$t('privateNetInstance.dataDisk') | withColon" v-if="form.checked">
					<card-group :showData="getDataDisks"
						:chooseTypeId="form.hasRowInfoDataRaidTypeId ? rowInfo.dataRaidTypeId : form.dataRaidTypeId"
						@getType="getSelectType">
					</card-group>
				</el-form-item>
				<el-form-item 
					v-if="form.checked"
					:label="$t('create.text.dataDiskCapacity') | withColon">
					<span class="c333">{{dataDiskCapacity}}</span>
					<span class="el-form-item-help">{{$t('create.text.sysDiskCapacityUnitTip')}}</span>
				</el-form-item>
				<el-form-item 
					id="dataPartition"
					:label="$t('retailInstance.text.dataDiskPartition') | withColon"
					v-if="form.checked">
					<template v-if="systemType === 'windows'">
						<span>{{$t('retailInstance.text.emptyPartition')}}</span>
					</template>
					<template v-else>
						<el-radio-group v-model="selectedDataDiskPartitionMode">
							<el-radio v-for="(item,index) in dataDiskPartitionModes" :key="index"
								:label="item.value">
								{{item.label}}
							</el-radio>
						</el-radio-group>
						<section 
							class="defaultPartition"
							v-if="selectedDataDiskPartitionMode === 'default'"
							v-html="defaultDataDiskPartitions">
						</section>
						<custom-partition
							ref="dataPartition"
							:visible="selectedDataDiskPartitionMode === 'custom'"
							:region="rowInfo.region"
							diskType="data"
							:partitionInfos="dataDiskPartitions"
							:otherPartitionInfos="systemDataPartitions"
							:isCentOS66="isCentOS66"
							:totalDiskSize="dataDiskSize">
						</custom-partition>
					</template>
				</el-form-item>
			</el-form>
			<custom-data :setCustomData.sync="form.advancedSetting"
				ref="advancedCustomData"
				:labelWidth="'120px'"
				:region="rowInfo.region"
				:selectedOS="form.operate"
				@getCustomData="getCustomData">	
			</custom-data>
			<div slot="footer" class="dialog-footer">
				<el-button @click="cancelReInstall">{{ $t('privateNetwork.cancel') }}</el-button>
				<el-button @click="sureReInstall('form')" type="primary">{{ $t('privateNetwork.sure') }}
				</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>

import cardGroup from '../create/subs/CardGroup.vue';
import { CustomPartitionSupportConstant} from '@/common/Constants';
import { validatePassword } from '@/common/dataHelper';
import loginMethods from '@/components/common/loginMethods/LoginMethods';
import CustomData from '@/components/common/customData/CustomData';
import retailLoginMethods from '@/components/common/loginMethods/RetailLoginMethods';
import CustomPartition from '@/components/common/customPartition/customPartition';
import { deepCopy } from '@/common/tool';

export default {
	name: 'ReSystem',
	components: {
		cardGroup,
		loginMethods,
		retailLoginMethods,
		CustomData,
		CustomPartition
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		rowInfo: {
			type: Object,
			default: () => {}
		},
		instanceType:{
			type:String,
			default:''
		},
		retailTipChange:{
			type:String,
			default:''
		}
	},

	data() {
		const valPas = (rule, value, callback) => {
			if (!value) {
				callback(this.$t('privateNetwork.passwordNotNull'));
			} else if (!validatePassword(value)) {
				callback(this.$t('privateNetInstance.passwordTip'));
			} else {
				callback();
			}
		};
		const valRepas = (rule, value, callback) => {
			if (!value) {
				callback(this.$t('privateNetwork.repasswordNotNull'));
			} else if (value !== this.form.password) {
				callback(this.$t('privateNetwork.repasswordNotPass'));
			} else {
				callback();
			}
		};
		const validatehostName = (rule, value, callback) => {
			if (value === '') {
				callback();
			}
			const hostRule = /(^((?!(^-)|(-$)|(--+)|(^\\.)|(\\.$)|(\\.\\.+))[0-9A-Za-z-.]){2,64}$)|(^\s*$)/;
			if (!hostRule.test(value)) {
				callback(new Error(this.$t('create.help.mainServerNameRule')));
			} else {
				callback();
			}
		};
		return {
			rules: {
				osTypeId: [
					{ required: true, message: this.$t('privateNetwork.osNotPass'), trigger: 'blur' },
				],
				password: [
					{ required: true, message: this.$t('privateNetwork.passwordNotNull'), trigger: 'blur' },
					{ validator: valPas, trigger: 'blur' }
				],
				repassword: [
					{ required: true, message: this.$t('privateNetwork.repasswordNotNull'), trigger: 'blur' },
					{ validator: valRepas, trigger: 'blur' }
				],
				selectKey: [
					{ required: true, trigger: 'change' }
				],
				hostname: [
					{
						validator: validatehostName,
						trigger: 'blur'
					}
				]
			},
			tableData: [],
			imageTypes: [],
			chargeModeType:'',
			selectedImageType:0,
			form: {
				oss: [],
				osTypes: {},
				operate: '',
				password: '',
				repassword: '',
				selectKey: '',
				sosKey: '',
				setKeyState: false,
				checked: false,
				hasRowInfoDataRaidTypeId: true,
				showData: [],
				chooseType: '',
				hostname: '',
				osTypeId: '',
				dataDisks: null,
				dataRaidTypeId: '',
				advancedSetting: false, // 高级设置开关(重装使用)
				userData: '' // 高级设置脚本内容，已base64过(重装使用)
			},
			isCentOS66:false,
			// 机型
			deviceType: '',
			deviceCustomPartitionSupport: true,
			// 系统盘总容量
			sysDiskCapacity: '',
			// 数据盘总容量
			dataDiskCapacity: '',
			systemDiskPartitionModes: [
				{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
				{id:2,label:this.$t('create.text.customPartitionModes'),value:'custom'},
			],
			selectedPartitionMode: 'default',
			defaultSystemDataPartitions: '',
			systemDataCustomPartitionVisible: false,
			systemDataPartitions: [],
			systemDiskSize: 0,
			dataDiskPartitionModes: [
				{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
				{id:2,label:this.$t('create.text.customPartitionModes'),value:'custom'},
			],
			selectedDataDiskPartitionMode: 'default',
			selectedDataRaid: {},
			defaultDataDiskPartitions: '',
			dataDiskPartitions: [],
			dataDiskSize: 0,
		};
	},
	computed: {
		getDataDisks() {
			return this.form.dataDisks;
		},
		systemType() {
			return this.form.operate === 'Windows' ? 'windows' : 'linux';
		},
	},
	watch: {
		selectedPartitionMode(newValue, oldVlue) {
			if (newValue === 'default') {
				this.resetDiskCapacity('system');
			}
		},
		selectedDataDiskPartitionMode(newValue, oldVlue) {
			if (newValue === 'default') {
				this.resetDiskCapacity('data');
			}
		},
		selectedImageType (newValue, oldVlue) {
			this.form.osTypeId = '';
			this.systemDataPartitions = [];
			this.dataDiskPartitions = [];
			this.defaultSystemDataPartitions = '';
			this.defaultDataDiskPartitions = '';
			this.getAvailableOS(this.rowInfo);
		},
	},
	created() {
		this.getInstanceInfo();
	},
	methods: {
		/**
		 * 获得组件传出的值
		 * @param loginType
		 * @param password
		 */
		getPassword(loginType, password, selectKey,sosKey) {
			this.form.loginType = loginType;
			this.form.password = password;
			this.form.selectKey = selectKey;
			this.form.sosKey = sosKey;
		},
		/**
		 * 根据raid类型和机型返回展示名称
		 * @param raidType
		 * @param deviceType
		 * @return {*}
		 */
		getDataDiskCardTitle(raidType, deviceType) {
			if (raidType === 'RAID51H') {
				if (deviceType === 'cps.c.perf3') {
					return 'RAID 5+1 hotspare';
				} if (deviceType === 'cps.s.perf1') {
					return 'RAID 5+1 hotspare+NO RAID';
				}
			} else {
				return raidType;
			}
		},
		/**
		 * 获得通过校验的输入内容
		 * 且内容已经base64过了
		 */
		getCustomData(value) {
			this.form.userData = value;
		},
		systemChange() {
			this.form.osTypeId = '';
			this.systemDataPartitions = [];
			this.dataDiskPartitions = [];
			this.defaultSystemDataPartitions = '';
			this.defaultDataDiskPartitions = '';
		},
		// 数据盘选项操作
		getSelectType(item) {
			this.selectedDataRaid = item;
			this.resetDataPartitionModes(item);
			let dataDiskCapacity = item.availableValue;
			this.calculateDiskCapacity('data',dataDiskCapacity);
			this.resetDiskCapacity('data');
			this.form.dataRaidTypeId = item.raidTypeId;
			this.form.dataRaidType = item.raidType;
		},
		handleClose() {
			this.$emit('update:visible', false);
		},
		cancelReInstall() {
			this.handleClose();
		},
		locateToErrorElement(id) {
			$('.re-system').scrollTop($('.re-system').scrollTop() + $(`#${id}`).offset().top
				- $('.re-system').offset().top - 30);
		},
		/**
		 * 重装系统时点击确定
		 * 增加了高级设置form
		 * 两个form校验均通过后打开重装系统确认
		 */
		sureReInstall(formName) {
			let systemDiskPartitions = this.systemDataPartitions.filter(item => !item.isNoPartition).map(item => {
					return {
						"size": item.size,
	                    "format": item.format,
	                    "point": item.point
					}
			});
			if (this.selectedPartitionMode === 'default') {
				systemDiskPartitions = [];
			}  
			let dataDiskPartitions = this.dataDiskPartitions.filter(item => !item.isNoPartition).map(item => {
					return {
						"size": item.size,
	                    "format": item.format,
	                    "point": item.point
					}
			});
			if (this.selectedDataDiskPartitionMode === 'default') {
				dataDiskPartitions = [];
			}
			// 校验系统盘及数据盘自定义分区模式下是否保存
			if(this.$refs.systemPartition && this.$refs.systemPartition.isRowNotSaved) {
				this.locateToErrorElement('systemPartition');
				return false;
			}
			if(this.$refs.dataPartition && this.$refs.dataPartition.isRowNotSaved) {
				return false;
			}
			this.$refs[formName].validate((valid) => {
				let pass = true;
				if (valid) {
					this.$refs.loginMethods.$refs.loginForm.validate((threeValid) => {
						pass = threeValid;
					});
					if (!pass) {
						return false;
					}
					if (typeof this.$refs.advancedCustomData !== 'undefined') {
						this.$refs.advancedCustomData.$refs.advanced.validate((twoValid) => {
							pass = twoValid;
						});
					}
					if (!pass) {
						return false;
					}
					const formInfo = this.form;
					formInfo.systemDiskPartitions = systemDiskPartitions;
					formInfo.dataDiskPartitions = dataDiskPartitions;
					this.$emit('openReConfirm', formInfo, this.rowInfo);
					this.handleClose();
				} else {
					return false;
				}
			});
		},
		getSelectOsId(osId) {
			let osTypes = this.form.osTypes[this.form.operate];
			let osName = osTypes.filter((item) => item.osTypeId === osId).reduce((init, current) => {return init + current.osName},'')
			let spaceReg = /\s+/g;
			let selectOsName = osName.split(spaceReg).splice(0,2).join(' ');
			// CentOS 6.6自定义分区文件系统特殊处理标识
			if (selectOsName === 'CentOS 6.6') {
				this.isCentOS66 = true;
			} else {
				this.isCentOS66 = false;
			}
			if (!osId) {
				return
			}
			let customPartitionSupport = this.deviceCustomPartitionSupport;
			let isQcow2 = osTypes.filter(item => item.osTypeId === osId).every(item => item.format === 'qcow2');
			this.resetPartitionModes(customPartitionSupport && !isQcow2);
			this.resetDataPartitionModes(this.selectedDataRaid);
			this.getOSPartitions();
		},

		getOSPartitions() {	
			if (!this.rowInfo.region || !this.rowInfo.deviceType || !this.form.osTypeId) {
				return
			}

			const param = {
		        region: this.rowInfo.region,
		        deviceType: this.rowInfo.deviceType,
		        imageId: this.form.osTypeId
		    };
		    Ajax.get('/api/cps/getOSPartitions', param)
		    .then((res) => {
				let partitions = res.data.partitions;
				this.handleSystemPartitions(partitions);
				this.handleDataPartitions(partitions);
		    }).catch(async (err)=>{
		    	const { response } = err.data;
				try {
					const json = await response.json();
					this.$message.error(json.errmsg);
				} catch (ex) {
					console.log(ex);
				}
		    });
		},
		
		dealRemainSystemDataPartitions(diskSize,diskPartitions) {
			return diskSize - diskPartitions.filter(item => item.size !== 999999999).reduce((init,current) => {
				return init + current.size
			},0);
		},

		dealDefaultPartitions(partitions) {
			let defaultPartitions = deepCopy(partitions);
				defaultPartitions.map((item) => {
					item.size = item.isRemainDataPartition ? this.$t('retailInstance.text.allRemainingSpace') : `${item.size}GiB`;
				});
			return defaultPartitions.reduce((init,current) =>{
				return `${init}<strong class="ml5 mr5">${current.point}:</strong><span>${current.size},${current.format};</span>` 
			},'')
		},

		handlePartitions(remainPartitions, diskPartitions, type) {
			let hasRemainDataPartition = false;
			let systemDataPartitions = diskPartitions.map(item=>{
				item.isEditing = false;
				if (item.size === 999999999) {
					item.size = remainPartitions;
					item.isRemainDataPartition = true;
					hasRemainDataPartition = true;
				}
				item.size = item.size / 1024;
				return item
			});

			if (type === 'systemDisk') {
				this.defaultSystemDataPartitions = this.dealDefaultPartitions(systemDataPartitions);
			} else if (type === 'dataDisk'){
				this.defaultDataDiskPartitions = this.dealDefaultPartitions(systemDataPartitions);
			}
			systemDataPartitions.push({
				point: this.$t('retailInstance.text.emptyPartition'),
				isEditing: false,
				size: hasRemainDataPartition ? 0 : parseInt(remainPartitions / 1024,10) ,
				format: this.isCentOS66 ? 'ext3' : 'xfs',
				isNoPartition: true
			});
			systemDataPartitions.map(item => {
				if (item.point === '/') {
					item.minSize = 5;
				} else if (item.point === '/boot') {
					item.minSize = 1;
				} else {
					item.minSize = 0;
				}
				item.maxSize = item.size + (hasRemainDataPartition ? 0 : parseInt(remainPartitions / 1024,10))
				return item
			});
			return systemDataPartitions
		},

		handleSystemPartitions(partitions) {
			let remainSystemDataPartitions =  this.dealRemainSystemDataPartitions(this.systemDiskSize*1024, partitions.systemDiskPartitions);
			this.systemDataPartitions = this.handlePartitions(remainSystemDataPartitions, partitions.systemDiskPartitions, 'systemDisk');
		},

		handleDataPartitions(partitions) {
			let remainSystemDataPartitions =  this.dealRemainSystemDataPartitions(this.dataDiskSize*1024, partitions.dataDiskPartitions);
			this.dataDiskPartitions = this.handlePartitions(remainSystemDataPartitions, partitions.dataDiskPartitions, 'dataDisk');
		},
		resetDiskCapacity(type) {
			if (!this.rowInfo.region || !this.rowInfo.deviceType || !this.form.osTypeId) {
				return
			}
			const param = {
		        region: this.rowInfo.region,
		        deviceType: this.rowInfo.deviceType,
		        imageId: this.form.osTypeId
		    };
		    Ajax.get('/api/cps/getOSPartitions', param)
		    .then((res) => {
				let partitions = res.data.partitions;
				if (type === 'system') {
					this.handleSystemPartitions(partitions);
				} else if(type === 'data') {
					this.handleDataPartitions(partitions);
				}
		    }).catch(async (err)=>{
		    	const { response } = err.data;
				try {
					const json = await response.json();
					this.$message.error(json.errmsg);
				} catch (ex) {
					console.log(ex);
				}
		    });
		},
		calculateDiskCapacity(type, capacity) {
			let diskCapacity = Math.floor(capacity * 0.93);
			if (type === 'system') {
				this.sysDiskCapacity =  diskCapacity + 'GiB';
				this.systemDiskSize = diskCapacity;
			} else if (type === 'data') {
				this.dataDiskCapacity = diskCapacity + 'GiB';
				this.dataDiskSize = diskCapacity;
			}
		},
		getAvailableOS(instance) {
			const params = {
				region: instance.region,
				deviceType: instance.deviceType,
			};
			const osTypes = []; const operates = {}; const imageTypes = [];
			Ajax.get('/api/cps/getAvailableOS', params)
				.then((res) => {
					const data = res.data;
					let dataList = [];
					for (const i in data.oss) {
						const image = {};
						image.source = data.oss[i].source;
						image.sourceName = data.oss[i].sourceName;
						imageTypes.push(image)											
					}
					var obj = {};
					this.imageTypes = imageTypes.reduce(function(item, next) {
						obj[next.source] ? '' : obj[next.source] = true && item.push(next);
						return item;
					}, []);	
					dataList = data.oss.filter(item=>item.source === this.imageTypes[this.selectedImageType].source);
					dataList.forEach((os) => {
						if (!operates[os.osType]) {
							osTypes.push(os.osType);
							operates[os.osType] = [];
						}
						operates[os.osType].push(os);
					});
				})
				.catch()
				.finally(() => {
					this.form.osTypes = operates;
					this.form.oss = osTypes;
					this.form.operate = osTypes[0];
					console.log(this.form.oss,'this.form.oss')
				});
		},
		/**
		 * 获取可用raid类型
		 * 同时将from.dataRaidTypeId初始化为rowInfoRaidTypeId
		 * @param info
		 */
		getAvailableRaids(info) {
			const params = {
				region: info.region,
				deviceType: info.deviceType
			};
			Ajax.get('/api/cps/getAvailableRaids', params)
			.then((res) => {
				//  处理系统盘分区总容量及数据盘总容量
				let sysDiskCapacity = res.data.raids.filter( item => item.volumeType === 'system')[0].availableValue;
				let systemPartitionCount = res.data.raids.filter( item => item.volumeType === 'system')[0].systemPartitionCount;
				sysDiskCapacity = sysDiskCapacity / systemPartitionCount;
				this.calculateDiskCapacity('system',sysDiskCapacity);

				let dataDiskCapacity;

				const tempDataRaids = [];
				res.data.raids.forEach((raid) => {
					if (raid.volumeType === 'data') {
						if (raid.raidType === 'NORAID') {
							tempDataRaids.unshift(raid);
						} else {
							tempDataRaids.push(raid);
						}
					}
				});
				this.form.dataDisks = tempDataRaids;
				this.form.hasRowInfoDataRaidTypeId = tempDataRaids.some(item => item.raidTypeId === this.rowInfo.dataRaidTypeId);
				if (this.form.hasRowInfoDataRaidTypeId) {
					this.form.dataRaidTypeId = this.rowInfo.dataRaidTypeId;
					this.form.dataRaidType = this.rowInfo.dataRaidType;
					//  兼容处理noraid
					this.rowInfo.raidType = this.rowInfo.dataRaidType;
					this.selectedDataRaid = this.rowInfo;
					this.resetDataPartitionModes(this.rowInfo);
					dataDiskCapacity = tempDataRaids.filter(item => item.raidTypeId === this.form.dataRaidTypeId)[0].availableValue
				} else {
					this.form.dataRaidTypeId = tempDataRaids[0].raidTypeId;
					this.form.dataRaidType = tempDataRaids[0].raidType;
					this.selectedDataRaid = tempDataRaids[0];
					//  兼容处理noraid
					this.resetDataPartitionModes(tempDataRaids[0]);
					dataDiskCapacity = tempDataRaids[0].availableValue;
				}
				this.calculateDiskCapacity('data',dataDiskCapacity);
			});
		},

		// noraid数据盘分区模式特殊处理
		resetDataPartitionModes(item) {
			// 机型不支持自定义分区就不需要再对数据盘单独处理
			if (!this.deviceCustomPartitionSupport) {
				return
			}
			if (item.raidType === 'NORAID') {
				this.dataDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
				];
			} else {
				this.dataDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
					{id:2,label:this.$t('create.text.customPartitionModes'),value:'custom'},
				];
			}
			this.selectedDataDiskPartitionMode = 'default';
		},
		
		// 是否支持自定义分区
		resetPartitionModes(customPartitionSupport) {
			if (!customPartitionSupport) {
				this.systemDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'}
				];
				this.dataDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'}
				];
			} else {
				this.systemDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
					{id:2,label:this.$t('create.text.customPartitionModes'),value:'custom'},
				],
				this.dataDiskPartitionModes = [
					{id:1,label:this.$t('create.text.defaultPartitionModes'),value:'default'},
					{id:2,label:this.$t('create.text.customPartitionModes'),value:'custom'},
				];
			}
			this.selectedPartitionMode = 'default';
			this.selectedDataDiskPartitionMode = 'default';
		},
		getInstanceInfo() {
			this.tableData = [];
			this.form.showData = [];
			const params = {
				region: this.rowInfo.region,
				instanceId: this.rowInfo.instanceId,
				az: this.rowInfo.az
			};
			Ajax.get('/api/cps/getInstanceInfo', params)
			.then((res) => {
				if (res.data) {
					const detail = res.data.instance;
					const info = {
						region: this.rowInfo.region,
						deviceType: this.rowInfo.deviceType,
						name: detail.name,
						system: detail.systemDiskDesc,
						chargeType: this.transformCharge(detail.charge.chargeMode),
						expire: detail.charge.chargeExpiredTime,
						dataDiskDesc: detail.dataDiskDesc
					};
					let customPartitionSupport = detail.cpsConfiguration.deviceType.customPartitionSupport
					this.deviceCustomPartitionSupport = CustomPartitionSupportConstant[customPartitionSupport];
					this.resetPartitionModes(this.deviceCustomPartitionSupport);
					this.chargeModeType = detail.charge.chargeMode;
					this.getAvailableRaids(info);
					this.deviceType = detail.deviceType;
					this.tableData.push(info);
					this.form.hostname = detail.hostname;
					this.form.showData.push({
						raidType: detail.dataRaidType,
						description: detail.dataRaidType,
						volumeDetail: detail.dataDiskDesc
					});
				}
			});
			this.getAvailableOS(this.rowInfo);
			//this.getAvailableRaids(this.rowInfo);
		},
		transformCharge(payMethod) {
	      	switch (payMethod) {
		        case 'prepaid_by_duration':
		          return this.$t('privateNetInstance.monthAndYear');
		        case 'postpaid_by_duration':
		          return this.$t('create.text.byConfigurations');
		        default:
		          return '';
	      	}
	    },
	}
};
</script>

<style lang="scss" scoped>
	.re-system{
		.jc-radio-button__orig-radio:checked+.jc-radio-button__inner {
			background: #2C68FF !important;
			color: #fff;
		}

		.basic-resys-login {
			.el-select {
				width: 320px;
			}
		}

		.title-position {
			margin-bottom: 20px;
			margin-top: 20px;
		}

		.el-form-item.is-hint {
			margin-bottom: 24px;
		}

		.el-alert--error {
			background-color: #fff2f4;
			border-color: #e15a5f;
			color: #e15a5f;
		}

		.form-position {
			margin-top: 20px;

			.el-select {
				width: 320px !important;
			}

			.el-input {
				width: 320px !important;
			}
		}
		.c333 {
			color: #333;
		}
	}
</style>
<style lang="scss">
	.re-system{
		.defaultPartition {
			strong {
				color: #212121;
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/privateNetInstance/operates/ReSystem.vue